import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import axios from '../../dataClient/dataClient'
import { config } from '../../config'
import { billTypeMap } from '../../util/billingConsts'
import { formatPriceStr } from '../../util/billFormatting'
import { formatUsersToAssignDropdownOptions } from '../../util/userFormatting'
import { attachmentStatusDropdownOptions } from '../../util/attachmentConsts'
import { openFoiCommunicationInNewWindow } from '../../util/openInNewWindow'
import {
  Divider,
  FlexColumn,
  InputWrapperDiv,
  MedBGContainer,
  SpaceBetweenRowContainer,
  WhiteBGContainer,
  FlexEndContainer
} from '../styles/Containers'
import { TotalsContainer, TotalDue, TotalAmount, TableContainer } from '../styles/BillStyles'
import { BoldBodyText, BoldSpan } from '../styles/Text'
import { InfoMessage } from '../common/UserMessage'
import { SecondaryButton } from '../common/Button'
import Dropdown from '../inputFields/Dropdown'
import AttachmentReadOnlyHeading from '../foiAttachments/AttachmentReadOnlyHeading'
import BillLineItemsTableHeader from './BillLineItemsTableHeader'
import BillLineItemsReadOnly from './BillLineItemsReadOnly'

const DraftHeaderActions = styled(FlexEndContainer)`
  flex-wrap: wrap;
`

function getUserId(user) {
  if (!user) return ''
  if (typeof user === 'object') return user.id || ''
  return user
}

function Bill({ bill, showCommunication = false, users = [], handleUpdateDraft = () => {} }) {
  const BackgroundComponent = showCommunication || bill.billState === 'DRAFT' ? WhiteBGContainer : MedBGContainer
  const isExternalSent = !showCommunication && bill.billState === 'SENT'

  const assignedToOptions = formatUsersToAssignDropdownOptions(users)
  const assignedToValue = getUserId(bill.assignedTo)
  const selectedAssignedToValue = assignedToOptions.some(option => option.value === assignedToValue) ? assignedToValue : ''

  const billAttachment = {
    ...bill,
    name: `${billTypeMap[bill.type]} ${bill.secondaryId}`,
    updatedAt: bill.feeLastModified
  }

  const patchAssignee = selected => {
    const updatedValue = selected !== '' ? selected : null
    return axios.patch(`/bill/${bill.id}`, { assignedTo: updatedValue }).then(() => handleUpdateDraft(bill.id))
  }

  const patchDraftStatus = selected => {
    return axios.patch(`/bill/${bill.id}`, { workflowStatus: selected }).then(() => handleUpdateDraft(bill.id))
  }

  const otherAccessMethod = config.foi.request.accessMethod.otherActiveType
  const hasOther = otherAccessMethod !== null

  const digitalLabel = `electronic copy ${config.foi.request.accessMethod.digitalLabel}`.trim()
  const physicalLabel = `paper copy ${config.foi.request.accessMethod.physicalLabel}`.trim()
  const otherLabel = hasOther ? `${otherAccessMethod.label} ${otherAccessMethod.pricingDetails}`.trim() : ''

  const partialLabel = `${digitalLabel} or ${physicalLabel}`
  const pricingLabel = hasOther ? `${partialLabel} or ${otherLabel}` : partialLabel

  return (
    <FlexColumn gap="15px">
      {isExternalSent ? (
        <AttachmentReadOnlyHeading state="SENT_EXTERNAL" attachment={billAttachment} />
      ) : bill.billState === 'DRAFT' ? (
        <SpaceBetweenRowContainer>
          <AttachmentReadOnlyHeading state="DRAFT_FEE" attachment={billAttachment} />
          <DraftHeaderActions gap="10px">
            <InputWrapperDiv minwidth="210px">
              <Dropdown
                options={attachmentStatusDropdownOptions}
                defaultValue={bill.workflowStatus}
                heading="Status"
                updateHeading="Update Status"
                onSelectOption={patchDraftStatus}
              />
            </InputWrapperDiv>
            <InputWrapperDiv minwidth="210px">
              <Dropdown
                options={assignedToOptions}
                defaultValue={selectedAssignedToValue}
                heading="Assigned To"
                updateHeading="Update Assignee"
                onSelectOption={patchAssignee}
              />
            </InputWrapperDiv>
          </DraftHeaderActions>
        </SpaceBetweenRowContainer>
      ) : (
        <SpaceBetweenRowContainer>
          <AttachmentReadOnlyHeading state="SENT_INTERNAL" attachment={billAttachment} />
          {bill.communicationId && (
            <SecondaryButton onClick={() => openFoiCommunicationInNewWindow(bill.foiId, bill.communicationId)}>
              View on Communication Page
            </SecondaryButton>
          )}
        </SpaceBetweenRowContainer>
      )}
      {bill.type === 'FEE_ESTIMATE' && (
        <InfoMessage>
          This is a fee <BoldSpan>estimate</BoldSpan>. The actual cost of providing the records may be higher or lower. Full payment will be required
          prior to providing response documents. Fees for the copying of any records contained in these files have not been included in this estimate
          and will be charged depending on your access method: {pricingLabel}.
        </InfoMessage>
      )}
      <BackgroundComponent padding="30px">
        <TableContainer>
          <BillLineItemsTableHeader />
          <Divider />
          <BillLineItemsReadOnly lineItems={bill.lineItems} />
        </TableContainer>
      </BackgroundComponent>
      <Divider />
      <TotalsContainer>
        <SpaceBetweenRowContainer>
          <BoldBodyText>Total</BoldBodyText>
          <TotalAmount>{formatPriceStr(bill.total)}</TotalAmount>
        </SpaceBetweenRowContainer>
        <SpaceBetweenRowContainer>
          <TotalDue>
            Total {bill.amountDue < 0 ? 'refunded' : 'due'}
            {bill.paymentDueDate ? ` ${format(bill.paymentDueDate, 'MMM-dd-yyyy')}` : ''} ({bill.percentDue}%)
          </TotalDue>
          <TotalDue>{formatPriceStr(bill.amountDue)}</TotalDue>
        </SpaceBetweenRowContainer>
      </TotalsContainer>
    </FlexColumn>
  )
}

const userSummaryPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  chipColor: PropTypes.string
})

Bill.propTypes = {
  bill: PropTypes.shape({
    secondaryId: PropTypes.string,
    foiId: PropTypes.string,
    type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
    billState: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
    workflowStatus: PropTypes.oneOf(['DRAFTING', 'AWAITING_APPROVAL', 'READY_TO_SEND']),
    percentDue: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    amountDue: PropTypes.number.isRequired,
    lineItems: PropTypes.array.isRequired,
    paymentDueDate: PropTypes.instanceOf(Date),
    feeLastModified: PropTypes.instanceOf(Date),
    sentAt: PropTypes.instanceOf(Date),
    modifiedBy: userSummaryPropType,
    sentBy: userSummaryPropType,
    communicationId: PropTypes.string
  }).isRequired,
  showCommunication: PropTypes.bool,
  users: PropTypes.arrayOf(userSummaryPropType),
  handleUpdateDraft: PropTypes.func
}

export default Bill
